import { Edit, Trash2, Download, Loader2, RotateCcw, Ban, FileSpreadsheet, DollarSign, Send, Clock, CheckCircle, AlertCircle, Mail } from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../../ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "../../ui/alert-dialog";
import { TableCell, TableRow } from "../../ui/table";
import { formatCurrency } from "../utils";
import { formatDate } from "../../../lib/formatters";

/**
 * Get payment status badge component
 */
const getPaymentStatusBadge = (billing) => {
  const status = billing.payment_status || "pending";
  const isVoid = billing.status === "void";
  
  if (isVoid) return null;
  
  const variants = {
    pending: { color: "bg-gray-100 text-gray-700 border-gray-200", icon: Clock, label: "Pending" },
    sent: { color: "bg-yellow-100 text-yellow-700 border-yellow-200", icon: Clock, label: "Sent" },
    partial: { color: "bg-amber-100 text-amber-700 border-amber-200", icon: AlertCircle, label: "Partial" },
    paid: { color: "bg-green-100 text-green-700 border-green-200", icon: CheckCircle, label: "Paid" },
    overdue: { color: "bg-red-100 text-red-700 border-red-200", icon: AlertCircle, label: "Overdue" }
  };
  
  const variant = variants[status] || variants.pending;
  const Icon = variant.icon;

  // Show "Sent {date} at {time}" tooltip on hover for sent/partial/paid/overdue
  // (all of which imply the billing has been sent at some point).
  let tooltip;
  if (status !== "pending" && billing.date_sent) {
    const d = new Date(billing.date_sent + "T00:00:00");
    const dateStr = isNaN(d.getTime())
      ? billing.date_sent
      : `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
    tooltip = billing.time_sent
      ? `Sent ${dateStr} at ${billing.time_sent}`
      : `Sent ${dateStr}`;
  }

  return (
    <Badge
      variant="outline"
      className={`text-xs ${variant.color}`}
      title={tooltip}
    >
      <Icon className="w-3 h-3 mr-1" />
      {variant.label}
    </Badge>
  );
};

/**
 * Individual billing table row with actions
 */
const BillingTableRow = ({
  billing,
  canManageJobs,
  generatingExcel,
  revising,
  onRecordPayment,
  onMarkSent,
  onEmailPayApp,
  onDownloadExcel,
  onEdit,
  onRevise,
  onDelete,
  gcBillingForm = false,
}) => {
  if (!billing) return null;
  const isVoid = billing.status === "void";
  const amountDue = billing.current_payment_due || billing.amount || 0;
  const amountReceived = billing.amount_received || 0;
  const balance = amountDue - amountReceived;
  // A billing is considered "not yet sent" when its payment_status is
  // pending (the default on creation). Once marked sent, it flips to "sent"
  // and the Record Payment action takes over.
  const isNotYetSent = (billing.payment_status || "pending") === "pending";

  return (
    <TableRow className={`table-row ${isVoid ? "opacity-60 bg-muted/30" : ""}`}>
      <TableCell className="font-medium">
        <div className="flex items-center gap-2">
          {billing.billing_number}
          {billing.revision_number > 0 && (
            <span className="text-xs text-muted-foreground">(Rev {billing.revision_number})</span>
          )}
          {isVoid && (
            <Badge variant="destructive" className="text-xs">
              <Ban className="w-3 h-3 mr-1" />
              VOID
            </Badge>
          )}
        </div>
      </TableCell>
      <TableCell>
        {getPaymentStatusBadge(billing)}
      </TableCell>
      <TableCell className={isVoid ? "line-through" : ""}>
        {formatDate(billing.application_date || billing.date_sent) || "-"}
      </TableCell>
      <TableCell className={`text-right font-mono font-medium ${isVoid ? "line-through" : ""}`}>
        {formatCurrency(amountDue, true)}
      </TableCell>
      <TableCell className={`text-right font-mono ${isVoid ? "line-through" : ""}`}>
        <span className={amountReceived > 0 ? "text-green-600" : "text-muted-foreground"}>
          {formatCurrency(amountReceived, true)}
        </span>
      </TableCell>
      <TableCell className={`text-right font-mono ${isVoid ? "line-through" : ""}`}>
        <span className={balance > 0 ? "text-amber-600 font-medium" : "text-green-600"}>
          {formatCurrency(balance, true)}
        </span>
      </TableCell>
      <TableCell>
        <div className="flex items-center justify-center gap-1">
          {canManageJobs && !isVoid && onEmailPayApp && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-sky-600"
              onClick={() => onEmailPayApp(billing)}
              title="Email to Billing contacts — opens a pre-filled draft; attach the signed PDF manually"
              data-testid={`email-pay-app-btn-${billing.id}`}
            >
              <Mail className="w-4 h-4" />
            </Button>
          )}

          {/* Before sent: Mark-as-Sent button (replaces $ Record Payment) */}
          {canManageJobs && !isVoid && isNotYetSent && onMarkSent && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-blue-600"
              onClick={() => onMarkSent(billing)}
              title="Mark as Sent"
              data-testid={`mark-sent-btn-${billing.id}`}
            >
              <Send className="w-4 h-4" />
            </Button>
          )}

          {/* After sent: Record Payment button */}
          {canManageJobs && !isVoid && !isNotYetSent && balance > 0 && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 text-green-600"
              onClick={() => onRecordPayment(billing)}
              title="Record Payment"
              data-testid={`record-payment-btn-${billing.id}`}
            >
              <DollarSign className="w-4 h-4" />
            </Button>
          )}
          
          {/* Download Dropdown - hide for GC billing */}
          {!gcBillingForm && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8"
                disabled={generatingExcel === billing.id}
                title="Download G702/G703"
                data-testid={`download-g702-btn-${billing.id}`}
              >
                {generatingExcel === billing.id ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Download className="w-4 h-4" />
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onDownloadExcel(billing)}>
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Download Excel (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          )}
          
          {canManageJobs && !isVoid && (
            <>
              <Button 
                variant="ghost" 
                size="icon" 
                className="h-8 w-8" 
                onClick={() => onEdit(billing.id)}
                title="Edit Application"
              >
                <Edit className="w-4 h-4" />
              </Button>
              {!gcBillingForm && (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="h-8 w-8 text-amber-600"
                    title="Revise Application"
                    disabled={revising === billing.id}
                  >
                    {revising === billing.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <RotateCcw className="w-4 h-4" />
                    )}
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Revise Application #{billing.billing_number}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will void the current application and create a new revision for editing.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => onRevise(billing)}
                      className="bg-amber-600 hover:bg-amber-700"
                    >
                      Create Revision
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
              )}
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete Application?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will permanently delete this application and all recorded payments.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction 
                      onClick={() => onDelete(billing.id)} 
                      className="bg-destructive text-destructive-foreground"
                    >
                      Delete
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          {isVoid && (
            <span className="text-xs text-muted-foreground px-2">
              {billing.void_reason || "Voided"}
            </span>
          )}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default BillingTableRow;
